import 'core-js/features/string/replace-all';
import 'core-js/features/string/match-all';
import React from "react";
import {Link} from "./injectors";

const domHelper = {
    scrollToAnchor: (anchor: string) => {
        const e = document.getElementById(anchor);
        if (e) e.scrollIntoView(true);
    },
};

export default domHelper;

export const acceptableImages = "image/jpeg, image/png";

export const acceptableDocuments = "image/jpeg, image/png, application/pdf";

/**
 * Extract a video ID from a YouTube or Dailymotion URL.
 * Returns { provider, videoId } or null if the URL is not a recognized video link.
 */
export function extractVideoId(url: string): { provider: 'youtube' | 'dailymotion', videoId: string } | null {
    // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID, youtube.com/shorts/ID
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    if (ytMatch) return { provider: 'youtube', videoId: ytMatch[1] };

    // Dailymotion: dailymotion.com/video/ID, dai.ly/ID
    const dmMatch = url.match(/(?:dailymotion\.com\/video\/|dai\.ly\/)([\w]+)/);
    if (dmMatch) return { provider: 'dailymotion', videoId: dmMatch[1] };

    return null;
}

const VideoEmbed: React.FC<{ provider: 'youtube' | 'dailymotion', videoId: string }> = ({ provider, videoId }) => {
    const src = provider === 'youtube'
        ? `https://www.youtube.com/embed/${videoId}`
        : `https://www.dailymotion.com/embed/video/${videoId}`;

    return (
        <div className="video-embed" onClick={e => e.stopPropagation()}>
            <iframe
                src={src}
                title="Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        </div>
    );
};

export function processLinks(value: (string | JSX.Element)[]): (string | JSX.Element)[] {
    return value.flatMap((v, idxFlat) => typeof v === 'string' ? regexifyString(/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/gim, v, (matches, idxReg) => {
        const video = extractVideoId(matches[0]);
        if (video) {
            return <VideoEmbed key={`${idxFlat}-${idxReg}`} provider={video.provider} videoId={video.videoId} />;
        }
        return <a key={`${idxFlat}-${idxReg}`} href={matches[0]} target="_blank" rel="noopener noreferrer">{matches[0]}</a>;
    }) : [v])
}

export function regexifyString(pattern: RegExp, input: string, decorator: (matches: string[], index: number) => string | JSX.Element): Array<(string | JSX.Element)> {
    /* const {
        pattern,
        decorator,
        input,
    } = props;*/
    const output: Array<(string | JSX.Element)> = [];

    let matchIndex = 0;
    let processedInput = input;
    let result = pattern.exec(processedInput);

    while (result !== null) {
        const matchStartAt = result.index;
        const match = result;

        const contentBeforeMatch = processedInput.substring(0, matchStartAt);
        const decoratedMatch = decorator(match, matchIndex);

        output.push(contentBeforeMatch);
        output.push(decoratedMatch);

        // clear processed content: before match, match
        processedInput = processedInput.substring(matchStartAt + match[0].length, processedInput.length + 1);

        pattern.lastIndex = 0;

        result = pattern.exec(processedInput);

        ++matchIndex;
    }

    if (processedInput) {
        output.push(processedInput);
    }

    return output;
}

export function processMentions(value: (string | JSX.Element)[], generateLink: boolean = true) {
    return value.flatMap(v => typeof v === 'string' ? regexifyString(/@\[(\w+)]\((\d+)\)/g, v, generateLink ? (matches, index) => {
        return <em className="ptn-em" key={index}><Link to={'connected.paanteon.user'}
                     params={{userId: matches[2], username: matches[1]}}>@{matches[1]}</Link></em>
    } : (matches) => {
        return `@${matches[1]}`
    }) : [v]);
}
